import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardList, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { formsApi } from '@/lib/api';
import { DueBadge, ResponseStatusPill, useOpenForm } from '@/components/forms/FormListRow';

/** Dashboard: forms waiting on me. Hidden entirely when there are none. */
export function MyFormsCard({ maxItems = 5 }: { maxItems?: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['myForms'],
    queryFn: () => formsApi.my(),
    staleTime: 30_000,
  });
  const { open, startingId } = useOpenForm();

  const assigned = data?.assigned ?? [];
  if (!isLoading && assigned.length === 0) return null;
  const shown = assigned.slice(0, maxItems);

  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <div className="flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-accent" />
          <span className="text-sm font-medium text-secondary">Forms to complete</span>
        </div>
        {!isLoading && <span className="text-xs text-grey-400 tabular-nums">({assigned.length})</span>}
      </div>

      {isLoading ? (
        <div className="px-4 py-8 flex justify-center">
          <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
        </div>
      ) : (
        <div className="divide-y divide-grey-100">
          {shown.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => open(r.formId, r)}
              disabled={startingId === r.formId}
              className="w-full text-left p-4 hover:bg-grey-50 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <span className="font-medium text-secondary group-hover:text-accent transition-colors truncate flex-1">{r.form.title}</span>
                <ResponseStatusPill status={r.status} />
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-grey-500">
                <span className="truncate">{r.form.project?.name || 'Solid Digital'}</span>
                <DueBadge dueDate={r.dueDate} />
              </div>
            </button>
          ))}
        </div>
      )}

      {assigned.length > maxItems && (
        <div className="px-4 py-3 border-t border-grey-100">
          <Link to="/forms" className="text-sm font-medium text-accent hover:text-accent-700 flex items-center gap-1">
            View all forms <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </Card>
  );
}
